import React from "react";

import { formatDate, formatTime } from "../format";

/**
 * `Ф0` (кабинет ПЗС): общие состояния экрана.
 *
 * Макет «Система»: загрузка — скелет, а не текст «Загрузка…»; пустой список —
 * объяснение и ссылка в поддержку; сбой фонового обновления — индикатор в шапке,
 * экран и последние данные остаются.
 */

/** Индикатор в шапке: «обновлено ЧЧ:ММ:СС» или «Не удалось обновить». */
export function RefreshIndicator({
  updatedAt,
  failed,
  onRetry,
}: {
  updatedAt: Date | null;
  failed: boolean;
  onRetry?: () => void;
}) {
  return (
    <div
      className={`cabinet-refresh${failed ? " cabinet-refresh--failed" : ""}`}
      role="status"
      aria-live="polite"
      data-failed={failed ? "true" : "false"}
    >
      <span className="cabinet-refresh__dot" aria-hidden="true" />
      {failed ? (
        <>
          <span>Не удалось обновить · данные на</span>
          <span className="cabinet-num">{formatTime(updatedAt)}</span>
          {onRetry ? (
            <button
              type="button"
              className="cabinet-btn cabinet-btn--small"
              onClick={onRetry}
            >
              Обновить
            </button>
          ) : null}
        </>
      ) : (
        <>
          <span>обновлено</span>
          <span className="cabinet-num">{formatTime(updatedAt)}</span>
        </>
      )}
    </div>
  );
}

/** Загрузка — скелет по форме будущего экрана. */
export function CabinetSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div
      className="cabinet-skeleton"
      aria-busy="true"
      aria-live="polite"
      data-testid="cabinet-skeleton"
    >
      <span className="cabinet-visually-hidden">Загрузка</span>
      <div className="cabinet-skeleton__title" />
      <div className="cabinet-skeleton__hero" />
      <div className="cabinet-skeleton__grid">
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className="cabinet-skeleton__tile" />
        ))}
      </div>
    </div>
  );
}

/** Пустой список: что случилось и что делать. */
export function CabinetEmpty({
  title,
  children,
  icon = true,
}: {
  title: string;
  children?: React.ReactNode;
  icon?: boolean;
}) {
  return (
    <div className="cabinet-empty">
      {icon ? (
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="5" y="3" width="10" height="18" rx="2" />
          <path d="M15 9h2a2 2 0 0 1 2 2v5a1.5 1.5 0 0 0 3 0V8l-3-3" />
        </svg>
      ) : null}
      <b className="cabinet-empty__title">{title}</b>
      {children ? <p className="cabinet-empty__text">{children}</p> : null}
    </div>
  );
}

export type AccountBannerModel = {
  tone: "danger" | "neutral";
  title: string;
  text: string;
  action: "cancel-deletion" | null;
};

/**
 * Плашка состояния учётки — по `ЖЦ-01`.
 *
 * До кабинета доходят три состояния: «активна» (плашки нет), «доступ закрыт» и
 * «поставлена на удаление». Остальные закрывают вход ещё на странице входа.
 */
export function accountBannerOf(user: {
  accountState?: string | null;
  deletionDueAt?: string | null;
} | null | undefined): AccountBannerModel | null {
  const state = user?.accountState ?? "active";

  if (state === "deletion_requested") {
    const due = formatDate(user?.deletionDueAt);

    return {
      tone: "danger",
      title:
        due === "—"
          ? "Учётная запись поставлена на удаление"
          : `Учётная запись будет удалена ${due}`,
      text: "До этой даты удаление можно отменить — здесь, после входа.",
      action: "cancel-deletion",
    };
  }

  if (state === "access_closed") {
    return {
      tone: "neutral",
      title: "Доступ закрыт",
      text: "Данные машиномест недоступны. Если это ошибка — напишите в поддержку.",
      action: null,
    };
  }

  return null;
}

/** Плашка над содержимым кабинета. */
export function AccountBanner({
  model,
  onCancelDeletion,
  cancelling = false,
  error = null,
}: {
  model: AccountBannerModel;
  onCancelDeletion?: () => void;
  cancelling?: boolean;
  error?: string | null;
}) {
  return (
    <div className={`cabinet-banner cabinet-banner--${model.tone}`} role="alert">
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v6M12 16h.01" />
      </svg>
      <div className="cabinet-banner__body">
        <b className="cabinet-banner__title">{model.title}</b>
        <p className="cabinet-banner__text">{model.text}</p>
        {error ? <p className="cabinet-banner__error">{error}</p> : null}
        {model.action === "cancel-deletion" && onCancelDeletion ? (
          <button
            type="button"
            className="cabinet-btn cabinet-btn--primary"
            onClick={onCancelDeletion}
            disabled={cancelling}
          >
            {cancelling ? "Отменяем…" : "Отменить удаление"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
